
import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';

const features = [
  { title: 'BridgeBot Chat 🤖', details: '73% of teens prefer AI conversations initially; crisis detection alerts faster than humans.' },
  { title: 'Daily Quests 🎯', details: 'Gamified micro-challenges take 3-5 minutes and increase engagement by 67%.' },
  { title: 'Biofeedback 💓', details: 'HRV training lowers cortisol by 32%, aiding teenage brain development.' },
  { title: 'FoodScan 🍎', details: 'Omega-3s support brain health; visual nutrition awareness improves by 67%.' },
  { title: 'SleepSense 😴', details: 'Sleep deprivation raises depression risk by 38%, anxiety by 55% in teens.' },
  { title: 'Social Browser 📱', details: 'Minimizes harmful social comparison and addictive behaviors by 41%.' },
  { title: 'Focus Bubble ⏰', details: '20-minute digital detox restores dopamine sensitivity and resilience.' },
  { title: 'Peer Support 👥', details: 'Structured peer mentoring reduces suicide risk by 50%.' },
  { title: 'Universal Screening 📊', details: 'PHQ-2/GAD-2 screens have 89% accuracy; enable early support and intervention.' },
  { title: 'XP & Rewards 🏆', details: 'Positive reinforcement increases task completion by 42%, rewiring thought patterns.' },
];

const FeatureHighlights = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (feature) => {
    setModalContent(feature);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setModalContent(null);
  };

  return (
    <FeaturesSection id="features">
      <Container>
        <SectionHeading>10 Research-Backed Features - Click to Explore</SectionHeading>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index} onClick={() => openModal(feature)}>
              {feature.title}
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
      {modalContent && (
        <StyledModal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          contentLabel="Feature Details"
        >
          <ModalTitle>{modalContent.title}</ModalTitle>
          <ModalText>{modalContent.details}</ModalText>
          <CloseButton onClick={closeModal}>Close</CloseButton>
        </StyledModal>
      )}
    </FeaturesSection>
  );
};

export default FeatureHighlights;

const FeaturesSection = styled.section`
  background-color: var(--background-dark);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 4rem;
  color: var(--primary-blue-dark);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: var(--background-light);
  color: var(--primary-blue-dark);
  padding: 1.5rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 25px rgba(74, 144, 226, 0.25);
  }
`;

// Reusing Modal styles from ResearchFoundation section
const StyledModal = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  padding: 40px;
  border-radius: 15px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  outline: none;
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  color: var(--primary-blue-dark);
  margin: 0 0 1rem 0;
`;

const ModalText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--text-dark);
  margin-bottom: 2rem;
`;

const CloseButton = styled.button`
  background: var(--primary-blue-light);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--primary-blue-dark);
  }
`;
